import type { Route } from 'next';
import { useSearchParams } from 'next/navigation';

import { routes } from '~constants/routes';
import { useAuthActionsContext } from '~context/auth';
import {
	getRegisterInboundByPath,
	getRegisterOutboundByPath,
} from '~features/annals/getRegisterEventByPath';
import { useNavigationLinksAnalytics } from '~hooks/useNavigationLinks/useNavigationLinksAnalytics';
import { useSession } from '~hooks/useSession';
import { usePathname, useRouter } from '~i18n/navigation';

import { useNavigationPaths, useNavigationPathsSideMenu } from './paths';
import type {
	ExtendedNavigationElement,
	ExtendedNavigationPath,
	NavigationPath,
} from './types';

export const useNavigationLinks = (sideMenu = false) => {
	const { isDemoUser, isLoading } = useSession();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const navigationPaths = useNavigationPaths(isDemoUser, isLoading);
	const navigationPathsSideMenu = useNavigationPathsSideMenu(
		isDemoUser,
		isLoading
	);

	const { openRegister } = useAuthActionsContext();
	const router = useRouter();

	const { annalsNavigationClick } = useNavigationLinksAnalytics(sideMenu);

	const isActive = (
		targetPaths: string[],
		ignoreQueryParams: { [key: string]: string } = {}
	) => {
		return targetPaths.some((targetPath) => {
			if (targetPath === routes.explore.home) {
				return Object.values(routes.explore).includes(pathname as Route);
			}

			const url = new URL(`https://example.com${targetPath}`);
			const targetPathname = url.pathname;
			const targetParams = url.searchParams;

			const pathMatches = pathname.startsWith(targetPathname);

			const queryMatches = Array.from(targetParams.keys()).every(
				(key) => searchParams.get(key) === targetParams.get(key)
			);

			const ignoreMatches = Object.entries(ignoreQueryParams).every(
				([key, value]) => searchParams.get(key) !== value
			);

			return pathMatches && queryMatches && ignoreMatches;
		});
	};

	const links: ExtendedNavigationElement[] = [];
	const mapPath = (item: NavigationPath) => {
		const {
			path,
			isDemo,
			withoutQuery,
			isNativeLink,
			additionalActivePaths = [],
		} = item;

		const handle = (e: React.SyntheticEvent) => {
			annalsNavigationClick(path)();

			const cb = () => {
				return router.push(path, { scroll: false });
			};

			if (!isDemo && isDemoUser) {
				e.preventDefault();
				openRegister({
					cb,
					analytics: {
						inbound: getRegisterInboundByPath(path),
						outbound: getRegisterOutboundByPath(path),
					},
				});
			} else if (!isNativeLink) {
				e.preventDefault();
				cb();
			}
		};

		return {
			...item,
			active: isActive([path, ...additionalActivePaths], withoutQuery),
			handle,
		};
	};
	(sideMenu ? navigationPathsSideMenu : navigationPaths).forEach((item) => {
		if ('children' in item) {
			const children: ExtendedNavigationPath[] = item.children.map((child) =>
				mapPath(child)
			);
			const active = children.some((child) => child.active);
			links.push({
				...item,
				children,
				active,
				handle: () => null,
			});
		} else {
			links.push(mapPath(item));
		}
	});

	return links;
};
